import {Choice} from 'platform/components';

import {MutableRefObject, useState} from 'react';

import i18n from '@dms/i18n';

import {useSourcingType} from '../../../../../../hooks/useSourcingType';
import {
  FilterFieldName,
  getFilterLabels,
  getFilterSections,
} from '../../../FilterEditor/utils/getFilterSections';
import {isFilterAvailable} from '../../../FilterEditor/utils/isFilterAvailable';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {useFilterSearchValues} from './hooks/useFilterSearchValues';

const MAX_VALUES_PER_FIELD = 30;
const normalizeSearch = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();

interface FilterSearchProps {
  onEdit: (field: FilterFieldName) => void;
  tilesWrapperRef: MutableRefObject<HTMLDivElement | null>;
}

export function FilterSearch(props: FilterSearchProps) {
  const [search, setSearch] = useState('');
  const {filters} = useFilters();
  const source = useSourcingType();
  const {values, isLoading} = useFilterSearchValues();
  const labels = getFilterLabels();
  const sections = getFilterSections();
  const terms = normalizeSearch(search).trim().split(/\s+/).filter(Boolean);
  const matches = (text: string) => terms.every((term) => normalizeSearch(text).includes(term));
  const fields = sections
    .flatMap((section) => section.fields)
    .filter((field) => isFilterAvailable(field, filters, source));
  const sectionOptions = sections
    .filter((section) => matches(section.label))
    .map((section) => ({
      value: `section:${section.id}`,
      label: section.label,
      select: () =>
        props.tilesWrapperRef.current
          ?.querySelector(`[id="${section.id}"]`)
          ?.scrollIntoView({behavior: 'smooth'}),
    }));
  const fieldOptions = fields
    .filter((field) => matches(labels[field]))
    .map((field) => ({
      value: `field:${field}`,
      label: labels[field],
      select: () => props.onEdit(field),
    }));
  const groups = [
    {label: i18n.t('entity.audits.checklist.sections'), options: sectionOptions},
    {label: i18n.t('page.filters.labels.filters'), options: fieldOptions},
    ...fields.map((field) => ({
      label: labels[field],
      options:
        terms.length === 0
          ? []
          : values
              .filter((item) => item.field === field && matches(`${labels[field]} ${item.label}`))
              .slice(0, MAX_VALUES_PER_FIELD),
    })),
  ].filter((group) => group.options.length > 0);

  return (
    <Choice
      value={null}
      aria-label={i18n.t('general.labels.search')}
      placeholder={i18n.t('general.labels.search')}
      options={groups}
      onChange={(value) =>
        groups
          .flatMap((group) => group.options)
          .find((option) => option.value === value)
          ?.select()
      }
      onInputChange={setSearch}
      filterOption={() => true}
      isLoading={isLoading}
      menuInPortal
      noOptionsMessage={() => i18n.t('general.labels.noSearchResults')}
      data-testid="sourcing-filter-search"
    />
  );
}
